import { useEffect, useState, SyntheticEvent } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Alert from 'react-bootstrap/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock, faEnvelope, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useAuth0 } from '@auth0/auth0-react'

import { useAppDispatch, useAppSelector } from 'hooks/redux'
import { getLogin } from 'store/slice/loginSlice'
import { post } from 'helpers/api'
import { PasswordField } from 'components/molecules/PasswordField'
import { getInit, setCurrentOfficeTests } from 'store/slice/initSlice'
import { useLazyGetDmeOfficeTestsQuery } from 'store/services/office'
import Logo from 'assets/img/logo2.png'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAppSelector((state) => state.login)
  const init = useAppSelector((state) => state.init)
  const dispatch = useAppDispatch()
  const [getDmeOffice] = useLazyGetDmeOfficeTestsQuery()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSso, setIsSso] = useState<boolean | undefined>()
  const { loginWithRedirect } = useAuth0()
  const destinationPath = location.state?.from?.pathname

  useEffect(() => {
    if (init?.userType === 'LAB') {
      if (destinationPath && destinationPath?.startsWith('/lab')) {
        return navigate(`${destinationPath}${location.state.from.search}`)
      }
      navigate('/lab')
    } else if (init?.userType === 'DME') {
      getDmeOffice({ id: Number(init?.currentOfficeId) })
        .unwrap()
        .then((tests) => dispatch(setCurrentOfficeTests(tests)))

      if (destinationPath && destinationPath?.startsWith('/dme')) {
        return navigate(`${destinationPath}${location.state.from.search}`)
      }
      navigate('/dme')
    } else if (init?.userType === 'PHYSICIAN_GROUP') {
      navigate('/physicians')
    } else if (login?.data?.access_token && !init?.pending && !init?.rejected) {
      // `rejected` stops the retry loop: every failed /me changes `init`, which
      // re-runs this effect. Without the guard an unreachable API turns that into
      // an unbounded request storm. It is cleared on the next successful sign-in
      // or logout, so a real retry still goes through.
      dispatch(getInit())
    }
  }, [login, init])

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault()
    if (!email) {
      return
    }
    localStorage.clear()
    if (false !== isSso && (await checkSso())) {
      return
    }
    if (!password) {
      return
    }
    dispatch(getLogin({ email, password }))
  }

  const ssoLogin = async () => {
    localStorage.setItem('login_email', email)
    await loginWithRedirect({
      authorizationParams: {
        login_hint: email,
        //organization: '',
      },
    })
  }

  const checkSso = async () => {
    try {
      const config = await post('/auth/config', { email })
      if (config?.sso) {
        await ssoLogin()
        return true
      }
    } catch (error) {}
    setIsSso(false)
    return false
  }

  function LoginLoading() {
    if (login?.pending) {
      return (
        <Alert variant="info">
          {' '}
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" /> Loading...
        </Alert>
      )
    }
    return null
  }

  return (
    <div>
      {/* Light Blue Header Bar */}
      <div style={{ backgroundColor: '#EDF6FA', padding: '20px 0', textAlign: 'center' }}>
        <img src={Logo} alt="TestSmarter Logo" style={{ maxHeight: '55px', maxWidth: '180px' }} />
      </div>

      <Container className="mt-5">
        <Row> &nbsp; </Row>
        <Row className="justify-content-md-center">
          <Col sm={5}>
            <form method="post" onSubmit={handleSubmit}>
              <div className="text-center">
                <h3 style={{ color: '#0069A8' }}>LOG IN</h3>
              </div>

              <div className="form-group">
                <label style={{ color: '#0F1822', fontWeight: '600' }}> Email address</label>
                <input
                  type="email"
                  className="form-control"
                  autoComplete="username"
                  placeholder="Enter email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {isSso === false && <PasswordField setPassword={setPassword} />}
              <Link to="/forgot-password" style={{ float: 'right' }}>
                Forgot Password?
              </Link>
              <div className="form-group" style={{ width: '10px' }}>
                <div className="custom-control custom-checkbox">
                  <input type="checkbox" className="custom-control-input" id="customCheck1" />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-block"
                style={{
                  backgroundColor: '#FFD700',
                  border: 'none',
                  borderRadius: '25px',
                  color: '#000000',
                  fontWeight: '600',
                  padding: '12px 20px',
                  fontSize: '16px',
                  boxShadow: 'none',
                }}>
                Sign In
              </button>
              <br />
              <LoginLoading />
              {login?.rejected && (
                <Alert variant="danger">
                  {' '}
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {login.error ?? 'Invalid Credentials'}
                </Alert>
              )}
            </form>
            {isSso === false && (
              <button className="btn btn-secondary mt-3" onClick={ssoLogin}>
                Sign In with SSO
              </button>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Login
